/** Activity summary of one partner, API key or tenant: calls per day over the last
 *  `days` (30 by default), the error rate, when it was last seen and the split by channel.
 *
 *  The per-day chart is a plain bar strip (the console has no chart library): one bar per
 *  UTC day, its height the day's calls, the error share drawn in the error colour on top.
 *  Each bar's tooltip has the numbers; the strip as a whole carries a text summary for
 *  screen readers. `last_seen` is the latest call of any time, not only in the window. */
import { Alert, Box, Chip, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import * as externalAccessApi from '@/api/externalAccess';
import type { ExternalAccessSummary as Summary } from '@/api/externalAccess';
import { formatDate, formatRelativeTime } from '@/utils/format';

import { CHANNEL_LABELS, CHANNELS, formatRate } from './externalAccessFormat';
import { resolveScope, type ExternalAccessScopeProps } from './externalAccessScope';

export type ExternalAccessSummaryProps = ExternalAccessScopeProps & { days?: number };

const count = new Intl.NumberFormat('en-US');

export function ExternalAccessSummary(props: ExternalAccessSummaryProps) {
  const days = props.days ?? 30;
  const scope = resolveScope(props);
  const query = useQuery({
    queryKey: ['external-access', 'summary', scope.kind, scope.id, days],
    queryFn: () => externalAccessApi.summary(scope.params, days),
  });

  if (query.isLoading) return <Skeleton variant="rounded" height={96} />;
  if (query.isError || !query.data) return <Alert severity="error">Couldn’t load the activity summary.</Alert>;
  return <SummaryView summary={query.data} />;
}

function SummaryView({ summary }: { summary: Summary }) {
  const { totals } = summary;
  const errors = totals.client_error + totals.server_error;
  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Stat label={`Calls, last ${summary.days} days`} value={count.format(totals.calls)} />
        <Stat
          label="Error rate"
          value={formatRate(totals.error_rate)}
          hint={`${count.format(errors)} errors (${count.format(totals.client_error)} client, ${count.format(totals.server_error)} server)`}
          tone={totals.error_rate != null && totals.error_rate >= 0.05 ? 'error.main' : undefined}
        />
        <Stat
          label="Last seen"
          value={totals.last_seen ? formatRelativeTime(totals.last_seen) : 'Never'}
          hint={totals.last_seen ? formatDate(totals.last_seen) : 'No call recorded yet'}
        />
        <Stat label="Denied" value={count.format(totals.denied)} hint="Refused keys or grants" />
        <Stat label="Throttled" value={count.format(totals.throttled)} hint="Refused by a rate limit" />
      </Box>

      <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }} aria-label="Calls by channel">
        {CHANNELS.map((c) => (
          <Chip
            key={c}
            size="small"
            variant="outlined"
            label={`${CHANNEL_LABELS[c]} ${count.format(summary.by_channel[c] ?? 0)}`}
            sx={{ opacity: summary.by_channel[c] ? 1 : 0.6 }}
          />
        ))}
      </Stack>

      <PerDayBars summary={summary} />
    </Stack>
  );
}

function Stat({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: string }) {
  const body = (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h6" component="div" sx={{ fontVariantNumeric: 'tabular-nums', color: tone }}>
        {value}
      </Typography>
    </Box>
  );
  return hint ? (
    <Tooltip title={hint} describeChild>
      {body}
    </Tooltip>
  ) : (
    body
  );
}

const BAR_AREA_PX = 64;

function PerDayBars({ summary }: { summary: Summary }) {
  const days = summary.per_day;
  const max = Math.max(1, ...days.map((d) => d.calls));
  const busiest = days.reduce<(typeof days)[number] | null>((m, d) => (m && m.calls >= d.calls ? m : d), null);
  const label =
    busiest && busiest.calls > 0
      ? `Calls per day over the last ${summary.days} days: ${count.format(summary.totals.calls)} in total, at most ${count.format(busiest.calls)} on ${busiest.day}.`
      : `No calls in the last ${summary.days} days.`;

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        Calls per day (UTC){' '}
        <Box component="span" sx={{ color: 'error.main' }}>
          ■
        </Box>{' '}
        errors
      </Typography>
      <Box
        role="img"
        aria-label={label}
        sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: BAR_AREA_PX, borderBottom: 1, borderColor: 'divider' }}
      >
        {days.map((d) => {
          const height = d.calls === 0 ? 0 : Math.max(2, Math.round((d.calls / max) * BAR_AREA_PX));
          const errorShare = d.calls ? Math.min(1, d.errors / d.calls) : 0;
          return (
            <Tooltip
              key={d.day}
              title={`${d.day}: ${count.format(d.calls)} calls · ${count.format(d.errors)} errors · ${count.format(d.denied)} denied · ${count.format(d.throttled)} throttled`}
            >
              <Box
                data-testid="per-day-bar"
                sx={{
                  flex: 1,
                  minWidth: 3,
                  height: Math.max(height, 1),
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: d.calls ? 'primary.main' : 'action.hover',
                  borderRadius: '2px 2px 0 0',
                  overflow: 'hidden',
                }}
              >
                {errorShare > 0 && <Box sx={{ height: `${errorShare * 100}%`, bgcolor: 'error.main' }} />}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      {days.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
          <Typography variant="caption" color="text.secondary">
            {days[0]!.day}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {days[days.length - 1]!.day}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
